import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  IconButton,
  Box,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { dialogPaperProps, missingModalField, modalActions } from "./styles";
import { useSnackbar } from "../../../../Components/SnackBar";
import { apiPostRequest } from "../../../../Helpers/Requests";
import endpoints from "../../../../Helpers/Endpoints";
import PointerEvents from "../../../../Utils/pointer";
import { useTranslation } from "react-i18next";

const MissingServiceModal = ({
  showMissingModal,
  setShowMissingModal,
  categories = [],
  searchTerm = "",
  subcategoryId = "",
  subcategoryName = "",
}) => {
  const { t } = useTranslation([
    "missing-service-modal",
    "categories",
    "services",
    "sku",
  ]);
  const snackbar = useSnackbar();
  const [serviceName, setServiceName] = useState(searchTerm);
  const [importantNote, setImportantNote] = useState("");
  const [categoryInputValue, setCategoryInputValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    if (subcategoryId && subcategoryName && categories.length) {
      const found = categories.find(
        (c) =>
          String(c.id) === String(subcategoryId) &&
          c.name.toLowerCase() === subcategoryName.toLocaleLowerCase()
      );
      if (found) setSelectedCategory(found);
    }
  }, [subcategoryId, categories, subcategoryName]);

  const handleSubmit = async () => {
    if (!serviceName) return;

    const trimmedInput = categoryInputValue?.trim()?.toLowerCase();
    const matchedCategory = categories.find(
      (cat) => cat.name.toLowerCase() === trimmedInput
    );

    const finalCategory = selectedCategory?.id
      ? selectedCategory
      : matchedCategory
      ? matchedCategory
      : null;

    const isCustomCategory = !finalCategory;

    const additionalInfo = importantNote
      ? isCustomCategory && categoryInputValue
        ? `${importantNote} - ${t("new_category")}${categoryInputValue}`
        : importantNote
      : isCustomCategory && categoryInputValue
      ? `${t("new_category")}${categoryInputValue}`
      : null;

    const body = {
      service_name: serviceName,
      additional_info: additionalInfo,
      category_id: finalCategory ? Number(finalCategory.id) : null,
      category_name: finalCategory
        ? t("finalCategory.name")
        : categoryInputValue,
    };

    try {
      PointerEvents.disable();
      const response = await apiPostRequest(
        endpoints.submitMissingServiceRequest,
        body
      );
      if (response.status === 200) {
        snackbar(t("missing_service_submitted_success"), "success");
        handleModalClose();
      }
    } catch (error) {
      snackbar(t("missing_service_submitted_error"), "error");
    } finally {
      PointerEvents.enable();
    }
  };

  const handleModalClose = () => {
    setShowMissingModal(false);
    setServiceName("");
    setImportantNote("");
    setSelectedCategory(null);
  };

  return (
    <Dialog
      open={showMissingModal}
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleModalClose();
      }}
      disableEscapeKeyDown
      PaperProps={{ sx: dialogPaperProps }}
      fullWidth
      maxWidth="sm"
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          px: 2,
          justifyContent: "space-between",
          borderBottom: "1px solid #EAEAEA",
        }}
      >
        <DialogTitle
          sx={{
            ml: "-17px",
            fontWeight: 600,
            fontSize: "18px",
            color: "#2F2B3DE5",
            p: 0,
            pl: 2,
            py: 2,
          }}
        >
          {t("tell_what_missing")}
        </DialogTitle>
        <IconButton onClick={handleModalClose}>
          <CloseIcon sx={{ fontSize: "19px" }} />
        </IconButton>
      </Box>

      {/* Body */}
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 3 }}
      >
        <Box>
          <Typography fontSize="15px" mb={1} color="#2F2B3DE5" fontWeight={500}>
            {t("what_service_or_category_missing")} *
          </Typography>
          <TextField
            fullWidth
            placeholder={t("missing_service_placeholder")}
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
            sx={missingModalField}
          />
        </Box>

        <Box>
          <Typography fontSize="15px" mb={1} fontWeight={500}>
            {t("important_information")}{" "}
            <Typography component="span" fontSize="13px" fontWeight={400}>
              ({t("optional")})
            </Typography>
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={4}
            placeholder={t("additional_details")}
            value={importantNote}
            onChange={(e) => setImportantNote(e.target.value)}
            sx={missingModalField}
          />
        </Box>

        <Box>
          <Typography color="#2F2B3DE5" fontSize="15px" mb={1} fontWeight={500}>
            {t("where_should_located")}{" "}
            <Typography component="span" fontSize="13px" fontWeight={400}>
              ({t("optional")})
            </Typography>
          </Typography>

          <Autocomplete
            freeSolo
            options={categories}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : t(option.name, { ns: ["categories", "services", "sku"] })
            }
            value={selectedCategory || null}
            inputValue={categoryInputValue}
            onInputChange={(event, newInputValue) => {
              setCategoryInputValue(newInputValue);
              if (selectedCategory && newInputValue !== selectedCategory.name) {
                setSelectedCategory(null);
              }
            }}
            onChange={(event, newValue) => {
              if (typeof newValue === "string") {
                setSelectedCategory({ name: newValue });
              } else if (newValue && typeof newValue === "object") {
                setSelectedCategory(newValue);
              } else {
                setSelectedCategory(null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t("select_or_type_category")}
                size="small"
                sx={missingModalField}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option?.id === value?.id &&
              option?.name.toLowerCase() === value?.name.toLowerCase()
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={modalActions}>
        <Button
          onClick={handleModalClose}
          variant="contained"
          sx={{
            backgroundColor: "#6C727F",
            color: "#fff",
            fontSize: "15px",
            borderRadius: "10px",
            px: 3,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#5b5f6b", boxShadow: "none" },
          }}
        >
          {t("back")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!serviceName}
          sx={{
            backgroundColor: "#248B70",
            color: "#fff",
            borderRadius: "10px",
            fontSize: "15px",
            px: 3,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": { backgroundColor: "#1f7b63", boxShadow: "none" },
            "&:disabled": { backgroundColor: "#e0e0e0" },
          }}
        >
          {t("submit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissingServiceModal;
